import React from 'react';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {ActorRefFrom} from 'xstate';
import type {IParticipantType} from '../../types/IParticipantType';
import type {identificationMachine} from '../../machines/identificationMachine';

import {RootMachineContext} from '../../machines/rootMachine';
import {selectPrimaryGuestName} from '../../selectors/event-details-selector';
import {
  selectButtonText,
  selectErrorText,
  selectRescheduleReason,
  selectSuccessText,
  selectTitle,
  selectIsReschedule,
} from '../../selectors/identification-selector';
import {selectRescheduleLimit} from '../../selectors/commonSelector';

import {SmallCard} from '../../lib/small-card/small-card';
import {MeetingDateTimeInfo} from '../meeting-date-time-info/meeting-date-time-info';
import {CardChildren} from '../../lib/card-children/card-children';

import {BodyMediumBold} from '@spaced-out/ui-design-system/lib/components/Text';
import {RadioButton} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import {ClickableCard} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import {getIdentificationFlowSchema} from './identification-flow.schema';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {ReasonInput} from '../reason-form/reason-input';
import {TEST_ID} from '../../utils/testId';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import css from './identification-flow.css';


export const IdentificationFlow = () => {
  const {t, makeKey} = useLabelTranslation();
  const rootActorRef = RootMachineContext.useActorRef();
  const identificationActor = rootActorRef.system.get(
    'identificationMachine',
  ) as ActorRefFrom<typeof identificationMachine>;

  const identificationState = useSelector(
    identificationActor,
    (state) => state,
  );

  const isReschedule = useSelector(identificationActor, selectIsReschedule);
  const identificationSchema = getIdentificationFlowSchema(t, isReschedule);
  const rescheduleReason = useSelector(
    identificationActor,
    selectRescheduleReason,
  );

  const {control, handleSubmit, formState, watch, trigger} = useForm({
    mode: 'onTouched',
    defaultValues: {
      participantType: undefined,
      reason: rescheduleReason || '',
    },
    resolver: zodResolver(identificationSchema),
  });
  const participantType = watch('participantType') as IParticipantType;
  const primaryGuestName = RootMachineContext.useSelector(
    selectPrimaryGuestName,
  );

  const rescheduleLimit = RootMachineContext.useSelector(selectRescheduleLimit);
  const title = useSelector(identificationActor, selectTitle);
  const successText = useSelector(identificationActor, (state) =>
    selectSuccessText(state, t, participantType),
  );
  const buttonText = useSelector(identificationActor, (state) =>
    selectButtonText(state, t, participantType),
  );
  const errorText = useSelector(identificationActor, (state) =>
    selectErrorText(state, t),
  );

  const isSubmitting = identificationState.matches('submitting');
  const isSuccess = identificationState.matches('success');
  const isAlreadyRequested = identificationState.matches('AlreadyRequested');
  const isError = identificationState.matches('retryFormSubmit');
  const isHost = participantType === 'HOST';

  const candidateRadioText = primaryGuestName
    ? t('I_am_guestName', {guestName: primaryGuestName})
    : t('I_am_the_guest_of_this_meeting', 'I am the guest of this meeting');

  const formSubmit = (values: z.infer<typeof identificationSchema>) => {
    identificationActor.send({
      type: 'form.submit',
      //TODO(Aditya) Even if user is interviewer or host, we should send the participant type as host for now
      participantType: values.participantType as IParticipantType,
      reason: values.reason,
    });
  };

  const isRadioDisabled = isSubmitting || isSuccess || isAlreadyRequested;

  const showRescheduleLimit = rescheduleLimit > -1 && isReschedule;

  return (
    <SmallCard
      icon="calendar"
      title={t(makeKey(title), title)}
      testId={generateTestId({
        base: TEST_ID.IDENTIFICATION_FLOW,
        slot: 'small-card',
      })}
      classNames={{content: css.identificationFlowContent}}
    >
      <MeetingDateTimeInfo />
      <CardChildren>
        <div className={css.identificationFlow}>
          {showRescheduleLimit && (
            <InContextAlert semantic={'information'}>{`${t(
              'Guest_will_get_an_email_to_reschedule_This_meeting_can_be_rescheduled_rescheduleLimit_more_times',
              {rescheduleLimit},
            )}.`}</InContextAlert>
          )}

          <Controller
            render={({field}) => (
              <>
                <ClickableCard
                  onClick={() => {
                    field.onChange('CANDIDATE');
                    trigger();
                  }}
                  classNames={{wrapper: css.identityCard}}
                  disabled={isRadioDisabled}
                  testId={generateTestId({
                    base: TEST_ID.IDENTIFICATION_FLOW,
                    slot: 'clickable-card-candidate',
                  })}
                >
                  <BodyMediumBold>
                    {/* Note: already getting translated from candidateRadioText */}
                    {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                    {candidateRadioText}
                  </BodyMediumBold>
                  <RadioButton
                    selectedValue={field.value}
                    value="CANDIDATE"
                    name="CANDIDATE"
                    disabled={isRadioDisabled}
                    testId={generateTestId({
                      base: TEST_ID.IDENTIFICATION_FLOW,
                      slot: 'radio-button-candidate',
                    })}
                  />
                </ClickableCard>
                <ClickableCard
                  onClick={() => {
                    field.onChange('HOST');
                    trigger();
                  }}
                  classNames={{wrapper: css.identityCard}}
                  disabled={isRadioDisabled}
                  testId={generateTestId({
                    base: TEST_ID.IDENTIFICATION_FLOW,
                    slot: 'clickable-card-host',
                  })}
                >
                  <BodyMediumBold>{`${t(
                    'I_am_the_host_of_this_meeting',
                    'I am the host of this meeting',
                  )}`}</BodyMediumBold>
                  <RadioButton
                    selectedValue={field.value}
                    value="HOST"
                    name="HOST"
                    disabled={isRadioDisabled}
                    testId={generateTestId({
                      base: TEST_ID.IDENTIFICATION_FLOW,
                      slot: 'radio-button-host',
                    })}
                  />
                </ClickableCard>
              </>
            )}
            name={'participantType'}
            control={control}
          />

          {isReschedule && isHost && !isAlreadyRequested && (
            <ReasonInput
              locked={isSuccess}
              control={control}
              reasonName="reason"
              required={true}
              disabled={isSubmitting}
              actionType="reschedule"
              participantType="HOST"
              testId={generateTestId({
                base: TEST_ID.IDENTIFICATION_FLOW,
                slot: 'reason-input',
              })}
            />
          )}

          {participantType && (
            <Button
              onClick={handleSubmit(formSubmit)}
              isLoading={isSubmitting}
              disabled={!formState.isValid}
              testId={generateTestId({
                base: TEST_ID.IDENTIFICATION_FLOW,
                slot: 'button',
              })}
            >
              {/* Note: already getting translated from buttonText */}
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {buttonText}
            </Button>
          )}

          {isSuccess ? (
            <InContextAlert
              semantic="information"
              testId={generateTestId({
                base: TEST_ID.IDENTIFICATION_FLOW,
                slot: 'in-context-alert-success',
              })}
            >
              {/* Note: already getting translated from successText */}
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {successText}
            </InContextAlert>
          ) : isError || isAlreadyRequested ? (
            <InContextAlert
              semantic="warning"
              testId={generateTestId({
                base: TEST_ID.IDENTIFICATION_FLOW,
                slot: 'in-context-alert-error',
              })}
            >
              {/* Note: already getting translated from getErrorText */}
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {errorText ||
                t(
                  'Something_went_wrong_please_refresh',
                  'Something went wrong, please refresh',
                )}
            </InContextAlert>
          ) : null}
        </div>
      </CardChildren>
    </SmallCard>
  );
};
